import { useMutation } from '@apollo/client';
import type { UpdateQuestionnaireTemplateVersionMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import {
  namedOperations,
  UpdateQuestionnaireTemplateVersionDocument,
} from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import {
  type UpdateQuestionnaireTemplateVersionInput,
  useUpdateQuestionnaireTemplateVersionTRPC,
} from './useUpdateQuestionnaireTemplateVersionTRPC';

interface UpdateQuestionnaireTemplateVersionOptions {
  /**
   * Threads Apollo request headers on the legacy GraphQL branch only
   * (e.g. `x-confirm-change-request`). The tRPC path ignores these —
   * `questionnaire_template_version` is a non-governed entity.
   */
  headers?: Record<string, string>;
}

/**
 * Flag-gated wrapper for the `updateQuestionnaireTemplateVersion`
 * mutation. Defaults to the legacy Apollo mutation and delegates to
 * the tRPC variant when the `trpc` feature flag is enabled.
 */
export const useUpdateQuestionnaireTemplateVersion = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [updateGraphQL, graphqlState] = useMutation(
    UpdateQuestionnaireTemplateVersionDocument,
    {
      update: (cache) => {
        evictField(cache, 'questionnaire_template_version');
        evictField(cache, 'questionnaire_template');
        evictField(cache, 'questionnaire_template_by_pk');
      },
      refetchQueries: [
        namedOperations.Query.getQuestionnaireTemplateById,
        namedOperations.Query.getQuestionnaireTemplates,
      ],
    }
  );

  const trpcMutation = useUpdateQuestionnaireTemplateVersionTRPC();

  const updateQuestionnaireTemplateVersion = async (
    input: UpdateQuestionnaireTemplateVersionInput,
    options?: UpdateQuestionnaireTemplateVersionOptions
  ): Promise<UpdateQuestionnaireTemplateVersionMutation> => {
    if (trpcEnabled) {
      return trpcMutation.updateQuestionnaireTemplateVersion(input);
    }

    const result = await updateGraphQL({
      variables: {
        Id: input.Id,
        object: {
          Version: input.Version,
          Status: input.Status,
          Schema: input.Schema,
          UISchema: input.UISchema,
          CustomAttributeData: input.CustomAttributeData ?? null,
        },
      },
      context: options?.headers ? { headers: options.headers } : undefined,
    });
    if (!result.data) {
      throw new Error('Failed to update questionnaire template version');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      updateQuestionnaireTemplateVersion,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    updateQuestionnaireTemplateVersion,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
